import React, {useCallback, useEffect, useMemo, useState} from 'react';

import Loader from '../../components/Loader/Loader';
import {
  deleteReport,
  gerReports,
  updateReportName,
  updateFavorite
} from '../../services/modules/ReportBuilder/ReportBuilderAPI';
import {reportDialog} from '../../store/reportStore/reportDialogStore';

import ReportDialogHost from './components/ReportDialogHost';
import DocumentPreview from './FilesLandingPage/DocumentPreview';

function ReportFavoriteStarIcon({filled}: {filled: boolean}) {
  if (filled) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
        <path
          d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ReportBuilder: React.FC = () => {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'recent' | 'favorites'>('recent');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [allReports, setAllReports] = useState<any[]>([]);
  const [deleteId, setDeleteId] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameOriginal, setRenameOriginal] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(true);

  useEffect(() => {
    if (!isRefresh) return;

    setIsLoading(true);
    gerReports()
      .then((res) => {
        setAllReports(res ?? []);
        setIsLoading(false);
        setIsRefresh(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsRefresh(false);
      });
  }, [isRefresh]);

  useEffect(() => {
    if (deleteId === '') return;

    deleteReport(deleteId)
      .then(() => {
        setDeleteId('');
        setIsRefresh(true);
      })
      .catch(() => {
        setDeleteId('');
      });
  }, [deleteId]);

  useEffect(() => {
    const closeMenu = () => {
      setMenuOpenId(null);
    };
    document.addEventListener('click', closeMenu);

    return () => {
      document.removeEventListener('click', closeMenu);
    };
  }, []);

  const handleDeleteFile = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setMenuOpenId(null);
    reportDialog.confirm({
      title: 'Delete document',
      message: 'Are you sure you want to delete this document?',
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: () => {
        setDeleteId(id);
      }
    });
  }, []);

  const handleToggleFavorite = useCallback(
    (e: React.MouseEvent, reportId: string) => {
      e.preventDefault();
      e.stopPropagation();

      let previousFavorite = false;
      let nextFavorite = false;

      setAllReports((prev) => {
        const row = prev.find((r) => r._id === reportId);
        previousFavorite = row?.favorite === true;
        nextFavorite = !previousFavorite;

        return prev.map((r) =>
          r._id === reportId ? {...r, favorite: nextFavorite} : r
        );
      });

      void updateFavorite(reportId, nextFavorite).catch(() => {
        setAllReports((prev) =>
          prev.map((r) =>
            r._id === reportId ? {...r, favorite: previousFavorite} : r
          )
        );
      });
    },
    []
  );

  const existingFilenames = useMemo(() => {
    const set = new Set<string>();
    (allReports ?? []).forEach((r: any) => {
      if (typeof r?.filename === 'string') set.add(r.filename);
    });

    return set;
  }, [allReports]);

  const startRename = useCallback((e: React.MouseEvent, file: any) => {
    e.stopPropagation();
    setMenuOpenId(null);
    setRenamingId(file?._id ?? null);
    const currentName = typeof file?.filename === 'string' ? file.filename : '';
    setRenameDraft(currentName);
    setRenameOriginal(currentName);
  }, []);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameDraft('');
    setRenameOriginal('');
  }, []);

  const commitRename = useCallback(
    async (file: any) => {
      const id = file?._id as string | undefined;
      if (!id) {
        cancelRename();

        return;
      }

      const raw = renameDraft.trim();
      if (!raw) {
        // don't allow empty names; just revert
        cancelRename();

        return;
      }

      if (raw === renameOriginal) {
        cancelRename();

        return;
      }

      // Ensure unique filename within current list (best-effort UX).
      let finalName = raw;
      if (existingFilenames.has(finalName)) {
        let counter = 1;
        while (existingFilenames.has(`${raw} (${counter})`)) counter += 1;
        finalName = `${raw} (${counter})`;
      }

      const prevAll = allReports;
      setAllReports((prev) =>
        prev.map((r: any) => (r?._id === id ? {...r, filename: finalName} : r))
      );
      cancelRename();

      try {
        await updateReportName(id, finalName);
        setIsRefresh(true);
      } catch (err) {
        setAllReports(prevAll);
      }
    },
    [allReports, cancelRename, existingFilenames, renameDraft, renameOriginal]
  );

  const handleRenameFile = useCallback(
    (e: React.MouseEvent, file: any) => {
      startRename(e, file);
    },
    [startRename]
  );

  const handleOpenFile = useCallback((id: string) => {
    window.open(`/editor?id=${id}`, '_blank');
  }, []);

  const filteredFiles = (allReports ?? []).filter((item: any) => {
    const matchesSearch = item?.filename
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (activeTab === 'favorites') return item?.favorite === true;

    return true;
  });

  return (
    <div className="filesLanding">
      <div className="files-landing-page">
        <section className="template-gallery">
          <div className="gallery-content">
            <div className="gallery-header">
              <h2 className="create-new-title">Create New</h2>
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                />
                <span className="search-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5f6368"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
              </div>
            </div>

            <div className="templates-grid">
              <div className="template-card">
                <a href="/editor" target="_blank">
                  <div className="template-preview blank-preview">
                    <span className="plus-icon">+</span>
                  </div>
                </a>
                <span className="template-name">Blank</span>
              </div>
            </div>
          </div>
        </section>

        <section className="recent-documents">
          <div className="recent-content">
            <div className="recent-header">
              <div className="recent-tabs">
                <button
                  className={`tab-btn ${
                    activeTab === 'recent' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setActiveTab('recent');
                  }}
                >
                  Recent
                </button>
                <button
                  className={`tab-btn ${
                    activeTab === 'favorites' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setActiveTab('favorites');
                  }}
                >
                  Favorites
                </button>
              </div>
              <div className="view-toggle">
                <button
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('grid');
                  }}
                  title="Grid view"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <rect x="3" y="3" width="8" height="8" rx="1" />
                    <rect x="13" y="3" width="8" height="8" rx="1" />
                    <rect x="3" y="13" width="8" height="8" rx="1" />
                    <rect x="13" y="13" width="8" height="8" rx="1" />
                  </svg>
                </button>
                <button
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('list');
                  }}
                  title="List view"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <rect x="3" y="4" width="18" height="3" rx="1" />
                    <rect x="3" y="10.5" width="18" height="3" rx="1" />
                    <rect x="3" y="17" width="18" height="3" rx="1" />
                  </svg>
                </button>
              </div>
            </div>

            {isLoading ? (
              <Loader />
            ) : (
              <>
                {filteredFiles.length === 0 ? (
                  <div className="no-files">
                    {activeTab === 'favorites'
                      ? 'No favorite documents found.'
                      : 'No recent documents found.'}
                  </div>
                ) : (
                  <div
                    className={`documents-grid ${
                      viewMode === 'list' ? 'list-view' : ''
                    }`}
                  >
                    {filteredFiles.map((file: any) => {
                      const isFavorite = file.favorite === true;
                      const isRenaming = renamingId === file._id;

                      return (
                        <div
                          key={file._id ?? file.id}
                          className={`document-card${
                            isFavorite ? ' document-card--favorited' : ''
                          }`}
                          onClick={() => {
                            if (isRenaming) return;
                            handleOpenFile(file._id);
                          }}
                        >
                          <div className="document-preview">
                            <DocumentPreview
                              fileId={file._id}
                              variant={viewMode === 'list' ? 'list' : 'grid'}
                              document={file.document}
                              watermark={file.watermark}
                              pageOrientation={file.pageOrientation}
                              pageMargins={file.pageMargins}
                            />
                            <button
                              type="button"
                              className="document-favorite-btn"
                              aria-pressed={isFavorite}
                              aria-label={
                                isFavorite
                                  ? 'Remove from favorites'
                                  : 'Add to favorites'
                              }
                              onClick={(e) => {
                                handleToggleFavorite(e, file._id);
                              }}
                            >
                              <ReportFavoriteStarIcon filled={isFavorite} />
                            </button>
                          </div>
                          <div className="document-info">
                            <div className="document-title">
                              {isRenaming ? (
                                <input
                                  type="text"
                                  value={renameDraft}
                                  autoFocus
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  onChange={(e) => {
                                    setRenameDraft(e.target.value);
                                  }}
                                  onBlur={() => {
                                    void commitRename(file);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      (
                                        e.currentTarget as HTMLInputElement
                                      ).blur();
                                    }
                                    if (e.key === 'Escape') {
                                      cancelRename();
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    border: '1px solid #c9ccd3',
                                    borderRadius: '4px',
                                    background: '#ffffff',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    outline: 'none',
                                    padding: '6px 8px'
                                  }}
                                />
                              ) : (
                                file.filename
                              )}
                            </div>
                            <div className="document-meta">
                              <span className="doc-icon">📄</span>
                              <span className="doc-date">
                                Opened{' '}
                                {new Date(file.updatedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="document-actions">
                              <button
                                className={`delete-btn ${
                                  menuOpenId === file._id ? 'active' : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenId(
                                    menuOpenId === file._id ? null : file._id
                                  );
                                }}
                              >
                                &#8942;
                              </button>
                              {menuOpenId === file._id && (
                                <div className="document-menu">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      handleRenameFile(e, file);
                                    }}
                                  >
                                    Rename
                                  </button>
                                  <button
                                    type="button"
                                    className="document-menu__delete"
                                    onClick={(e) => {
                                      handleDeleteFile(e, file._id);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      <ReportDialogHost />
    </div>
  );
};
export default ReportBuilder;
